import { apiClient } from '@app/api';
import { FormActionsMui } from '@app/components/form/FormActionsMui';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useSocialExportFactory } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { GetFragmentDto } from '@mi-front/core';
import { QueryView } from '@mi-front/ui';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

export function AudiogramSocialExportDialog(p: {
  platform: SocialPlatform;
  fragment: GetFragmentDto;
  show: boolean;
  variant: string;
  onHide: () => void;
  onSubmit: (data: any) => Promise<void>;
}) {
  const { $t } = useIntl();
  const useExport = useSocialExportFactory(p.platform);
  const { PublishForm: ExportForm } = useExport();

  const mediaUrlQuery = useQuery({
    queryKey: ['audiogram-media-url', p.fragment.id, p.variant],
    queryFn: () => apiClient.fragments.audiogramMediaUrl(p.fragment.id, p.variant),
  });

  return (
    <QueryView query={mediaUrlQuery} noLoader>
      {(data) => {
        return (
          <SocialExportContext.Provider value={{ mediaUrl: data }}>
            <Dialog open={p.show} onClose={p.onHide} fullWidth maxWidth="md" scroll="body">
              <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                {p.platform.Icon()}{' '}
                <span style={{ marginLeft: 8 }}>
                  <FormattedMessage
                    id="audiogram.exportToPlatform"
                    defaultMessage="Export audiogram to {platformName}"
                    values={{ platformName: p.platform.name }}
                  />
                </span>
                <IconButton
                  aria-label={$t({
                    id: 'common.close',
                    defaultMessage: 'Cerrar',
                  })}
                  onClick={p.onHide}
                  sx={{ ml: 'auto' }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <ExportForm
                  fragment={p.fragment}
                  videoUrl={data}
                  defaultSettings={undefined}
                  onUpload={async (formData) => {
                    await p.onSubmit(formData);
                  }}
                  actions={
                    <FormActionsMui
                      separator
                      saveIcon={<UploadIcon />}
                      saveLabel={$t({
                        id: 'socialExport.form.publish',
                        defaultMessage: 'Publicar',
                      })}
                      cancel
                    />
                  }
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={p.onHide}>
                  <FormattedMessage id="common.close" defaultMessage="Cerrar" />
                </Button>
              </DialogActions>
            </Dialog>
          </SocialExportContext.Provider>
        );
      }}
    </QueryView>
  );
}
